/**
 * Regras que fazem o relatório de Receita contar o mesmo dinheiro que o Financeiro.
 * Ver supabase/migrations/20261005120000_link_sales_to_financial_ledger.sql (troco, a receber)
 * e 20261006120000_partial_sale_returns_refund.sql (devoluções).
 */

export type SaleMethod = "DINHEIRO" | "PIX" | "CARTAO_CREDITO" | "CARTAO_DEBITO" | "A_RECEBER" | string;

export interface PaidSale {
  id: string;
  total: number | null;
}

export interface SalePaymentRow {
  sale_id: string;
  method: SaleMethod;
  amount: number | null;
  created_at?: string | null;
}

/** Lançamento do razão ligado a uma devolução, ou o estorno de um deles. */
export interface RefundEntryRow {
  id: string;
  amount: number | null;
  payment_method: string | null;
  sale_return_id: string | null;
  reversal_of: string | null;
}

export interface MethodAmount {
  method: string;
  amount: number;
  percentage: number;
}

const FINANCE_TO_SALE_METHOD: Record<string, SaleMethod> = {
  cash: "DINHEIRO",
  pix: "PIX",
  credit_card: "CARTAO_CREDITO",
  debit_card: "CARTAO_DEBITO",
};

const cents = (v: number) => Math.round(v * 100) / 100;

/**
 * Troco não é pagamento: o que foi entregue acima do total da venda sai do dinheiro,
 * do pagamento em dinheiro mais recente para o mais antigo (a mesma regra do razão).
 */
export function paymentsWithoutChange(sales: PaidSale[], payments: SalePaymentRow[]): SalePaymentRow[] {
  const totalBySale = new Map(sales.map((s) => [s.id, Number(s.total ?? 0)]));
  const bySale = new Map<string, SalePaymentRow[]>();
  for (const p of payments) bySale.set(p.sale_id, [...(bySale.get(p.sale_id) ?? []), p]);

  const out: SalePaymentRow[] = [];
  for (const [saleId, rows] of bySale) {
    const paid = rows.reduce((s, p) => s + Number(p.amount ?? 0), 0);
    let change = Math.max(0, cents(paid - (totalBySale.get(saleId) ?? paid)));
    const newestFirst = [...rows].sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
    for (const p of newestFirst) {
      let amount = Number(p.amount ?? 0);
      if (p.method === "DINHEIRO" && change > 0) {
        const cut = Math.min(amount, change);
        amount = cents(amount - cut);
        change = cents(change - cut);
      }
      out.push({ ...p, amount });
    }
  }
  return out;
}

/** Quanto cada forma de pagamento devolveu em reembolsos ainda valendo (estornos descontados). */
export function refundsByMethod(entries: RefundEntryRow[]): Map<SaleMethod, number> {
  const reversed = new Map<string, number>();
  for (const e of entries) if (e.reversal_of) reversed.set(e.reversal_of, (reversed.get(e.reversal_of) ?? 0) + Number(e.amount ?? 0));

  const out = new Map<SaleMethod, number>();
  for (const e of entries) {
    if (!e.sale_return_id || e.reversal_of) continue;
    const method = FINANCE_TO_SALE_METHOD[e.payment_method ?? ""];
    if (!method) continue;
    // reembolso é negativo; o estorno dele, positivo
    const refunded = -(Number(e.amount ?? 0) + (reversed.get(e.id) ?? 0));
    if (refunded > 0) out.set(method, cents((out.get(method) ?? 0) + refunded));
  }
  return out;
}

/** "Como te pagaram": pagamentos sem o troco, menos o que foi reembolsado em cada forma. */
export function paymentMethodTotals(
  sales: PaidSale[],
  payments: SalePaymentRow[],
  refundEntries: RefundEntryRow[] = [],
): MethodAmount[] {
  const agg = new Map<string, number>();
  for (const p of paymentsWithoutChange(sales, payments)) agg.set(p.method, (agg.get(p.method) ?? 0) + Number(p.amount ?? 0));
  for (const [method, refunded] of refundsByMethod(refundEntries)) agg.set(method, (agg.get(method) ?? 0) - refunded);

  const rows = [...agg.entries()].map(([method, amount]) => ({ method, amount: cents(amount) })).filter((r) => r.amount > 0);
  const total = rows.reduce((s, r) => s + r.amount, 0);
  return rows
    .map((r) => ({ ...r, percentage: total > 0 ? (r.amount / total) * 100 : 0 }))
    .sort((a, b) => b.amount - a.amount);
}

export interface SaleReturnRow {
  sale_id: string;
  subtotal: number | null;
  total: number | null;
}

/** Devoluções concluídas somadas por venda: `subtotal` é o valor das linhas, `total` o reembolso. */
export function returnsBySale(rows: SaleReturnRow[]): Map<string, { subtotal: number; total: number }> {
  const out = new Map<string, { subtotal: number; total: number }>();
  for (const r of rows) {
    const cur = out.get(r.sale_id) ?? { subtotal: 0, total: 0 };
    out.set(r.sale_id, { subtotal: cents(cur.subtotal + Number(r.subtotal ?? 0)), total: cents(cur.total + Number(r.total ?? 0)) });
  }
  return out;
}

/** Receita líquida de uma venda: o total menos o que já foi reembolsado em devoluções. */
export function saleNet(sale: PaidSale, returns: Map<string, { total: number }>): number {
  return cents(Number(sale.total ?? 0) - (returns.get(sale.id)?.total ?? 0));
}

/** Ordem da lista de contas a receber: quem tem vencimento primeiro, por vencimento; depois por data da venda. */
export function compareReceivables(
  a: { dueOn: string | null; createdAt: string },
  b: { dueOn: string | null; createdAt: string },
): number {
  if (a.dueOn && b.dueOn) return a.dueOn.localeCompare(b.dueOn) || a.createdAt.localeCompare(b.createdAt);
  if (a.dueOn) return -1;
  if (b.dueOn) return 1;
  return a.createdAt.localeCompare(b.createdAt);
}
